import React, { FC } from 'react';

import { Controller } from 'react-hook-form';
import { NavLink, Outlet } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';

import Breadcrumbs from 'components/common/ui-kit/Breadcrumbs';
import Button from 'components/common/ui-kit/Button';
import ReactSelect from 'components/common/ui-kit/Select';
import useSubmitFormContext, {
  SubmitFormContextProvider,
} from 'hooks/common/useSubmitFormContext';
import useModeratorStatementsRoutesContext, {
  ModeratorStatementsRoutesContextProvider,
} from 'hooks/moderator/useModeratorStatementsRoutesContext';
import terms from 'i18n';
import { agreementOptions } from 'models/applications/applications';
import { SelectOption } from 'models/common';
import { renderDateString } from 'pages/Enrollment/common/column-renderers';
import SidenavPage from 'pages/SidenavPage';
import {
  findSelectOption,
  originalEducationDocumentSubmissionTypeOptions,
} from 'utils/enum-select';

import useAppDispatch from '../../../hooks/common/useAppDispatch';
import useAppSelector from '../../../hooks/common/useAppSelector';
import { isApplicationHasStatusWorkOrHigher } from '../../../hooks/moderator/applications/applicationEnumChecks';
import { useGetModeratorSelectProps } from '../../../hooks/moderator/applications/useGetModeratorSelectProps';
import { useInitializeStaffApplication } from '../../../hooks/moderator/applications/useInitializeStaffApplication';
import { useModeratorApplication } from '../../../hooks/moderator/applications/useModeratorApplication';
import {
  ApplicationEducationLevel,
  ApplicationFlag,
  ApplicationState,
} from '../../../models/applications/enum';
import { UserRole } from '../../../models/user';
import { showModal } from '../../../redux/actions';
import { selectCurrentApplication } from '../../../redux/selectors/staff/applications';

const originalDocumentTypeSelectOptions = [
  { label: terms.ABSENT, value: null },
  ...originalEducationDocumentSubmissionTypeOptions,
];

const applicationStateSelectOptionsAdmin = [
  { label: terms[ApplicationState.ACCEPTED], value: ApplicationState.ACCEPTED },
  { label: terms[ApplicationState.ALLOWED], value: ApplicationState.ALLOWED },
  {
    label: terms[ApplicationState.WITHDRAWN_APPROVED],
    value: ApplicationState.WITHDRAWN_APPROVED,
  },
  // {
  //   label: terms[ApplicationState.DIRECTIONS_EDITED],
  //   value: ApplicationState.DIRECTIONS_EDITED,
  // },
];

// const applicationStateSelectOptions = [
//   { label: terms[ApplicationState.ALLOWED], value: ApplicationState.ALLOWED },
//   {
//     label: terms[ApplicationState.DIRECTIONS_EDITED],
//     value: ApplicationState.DIRECTIONS_EDITED,
//   },
// ];

const ModeratorStatementsIdPage: FC = () => {
  useInitializeStaffApplication();
  const dispatch = useAppDispatch();

  const moderators = useGetModeratorSelectProps();
  const application = useAppSelector(selectCurrentApplication);

  const {
    role,
    state,
    control,
    expertState,
    coordinatorState,
    takeToWork,
    sendToExpert,
    sendToCoordinator,
    changeModerator,
    sendToEditing,
    rejectApplication,
    rejectApplicationEditing,
    acceptApplication,
    allowApplication,
    fields,
    approveByCoordinator,
    declineWithdraw,
    rejectByCoordinator,
    acceptWithdraw,
    withdrawByAdmin,
    rejectByExpert,
    approveByExpert,
    canSendToExpert,
    takeToWorkByExpert,
    takeToWorkByCoordinator,
    expertId,
    coordinatorId,
    navLink,
    stateTitle,
    sendToQueue,
    clearError,
    selectedEducationDocumentType,
    handleOriginalEducationDocumentTypeChange,
    originalSubmission,
    handleApplicationStateChangeChange,
    requestAdditionalFiles,
    selectedAgreement,
    handleHasAgreementChange,
    sspvoLink,
  } = useModeratorApplication(moderators);

  const openModal = () => {
    dispatch(
      showModal({
        name: 'CONFIRM_WITHDRAWN_APPLICATION',
        data: {
          text: terms.CONFIRM_WITHDRAWN_APPLICATION,
          applicationId: application?.id,
          func: withdrawByAdmin,
        },
      })
    );
  };

  // const renderStateControl = () => {
  //   if (role === UserRole.ADMIN) {
  //     return (
  //       <ReactSelect<SelectOption>
  //         className="moderator-contract-info__state-select"
  //         options={applicationStateSelectOptionsAdmin}
  //         value={{ value: state, label: stateTitle }}
  //         onChange={e => handleApplicationStateChangeChange(e.value)}
  //       />
  //     );
  //   }
  //   const allowedRoles = [
  //     UserRole.MODERATOR,
  //     UserRole.INSTITUTE_STAFF,
  //     UserRole.SPECIALIST,
  //   ];
  //   if (allowedRoles.includes(role) && state === ApplicationState.ALLOWED) {
  //     return (
  //       <ReactSelect<SelectOption>
  //         className="moderator-contract-info__state-select"
  //         options={applicationStateSelectOptions}
  //         value={{ value: state, label: stateTitle }}
  //         onChange={e => handleApplicationStateChangeChange(e.value)}
  //       />
  //     );
  //   }
  //   return <span>{stateTitle}</span>;
  // };

  return (
    <div className="moderator-statements-id-page">
      <div className="moderator-statements-id-page__header">
        <Breadcrumbs
          link="/admin/statements"
          className="moderator-statements-id-page__header-title"
          title="Проверка заявления"
        />
        {role === UserRole.ADMIN &&
          (isApplicationHasStatusWorkOrHigher(state, application) ? (
            <Controller
              name={fields.moderator.name}
              control={control}
              render={({ field }) => (
                <ReactSelect<SelectOption>
                  className="moderator-statements-id-page__header-select"
                  {...(field as object)}
                  options={moderators}
                  onChange={e => {
                    if (e) {
                      field.onChange(e);
                      changeModerator(e.value);
                    }
                  }}
                  placeholder="Модератор"
                />
              )}
            />
          ) : (
            <Button
              className="moderator-statements-id-page__header-btn"
              onClick={takeToWork}
            >
              Взять в работу
            </Button>
          ))}

        {(role === UserRole.MODERATOR ||
          role === UserRole.INSTITUTE_STAFF ||
          role === UserRole.SPECIALIST) &&
          (isApplicationHasStatusWorkOrHigher(state, application) &&
          !application?.moderatorId ? (
            <Button
              className="moderator-statements-id-page__header-btn"
              onClick={takeToWork}
            >
              Взять в работу
            </Button>
          ) : (
            <Button className="moderator-statements-id-page__header-btn">
              В работе
            </Button>
          ))}

        {role === UserRole.EXPERT &&
          (application?.epguInfo?.createdFromEpgu
            ? state !== ApplicationState.DRAFT
            : state === ApplicationState.IN_WORK) &&
          (expertId ? (
            <Button className="moderator-statements-id-page__header-btn">
              В работе
            </Button>
          ) : (
            <Button
              className="moderator-statements-id-page__header-btn"
              onClick={takeToWorkByExpert}
            >
              Взять в работу
            </Button>
          ))}

        {role === UserRole.COORDINATOR &&
          (application?.epguInfo?.createdFromEpgu
            ? state !== ApplicationState.DRAFT
            : state === ApplicationState.IN_WORK) &&
          (coordinatorId ? (
            <Button className="moderator-statements-id-page__header-btn">
              В работе
            </Button>
          ) : (
            <Button
              className="moderator-statements-id-page__header-btn"
              onClick={takeToWorkByCoordinator}
            >
              Взять в работу
            </Button>
          ))}
      </div>

      <SubmitFormContextProvider>
        <ModeratorStatementsRoutesContextProvider>
          <SidenavPage
            useSubmitFormHook={useSubmitFormContext}
            useHooks={useModeratorStatementsRoutesContext}
            title=""
            next={false}
          >
            <Outlet />
          </SidenavPage>
        </ModeratorStatementsRoutesContextProvider>
      </SubmitFormContextProvider>

      {role === UserRole.SUPPORT && (
        <div className="moderator-contract-info">
          <div className="moderator-contract-info__footer">
            <div className="moderator-contract-info__footer-btn">
              <div className="moderator-contract-info__block">
                <NavLink to={navLink()} target="_blank">
                  <Button type="button">
                    Зайти в личный кабинет пользователя
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      )}

      {role !== UserRole.SUPPORT && (
        <div className="moderator-contract-info">
          <div className="moderator-contract-info__block">
            <h2>
              Статус заявки:{' '}
              {role === UserRole.ADMIN ? (
                <ReactSelect<SelectOption>
                  className="moderator-contract-info__state-select"
                  options={applicationStateSelectOptionsAdmin}
                  value={{
                    value: state,
                    label: stateTitle,
                  }}
                  onChange={e => {
                    handleApplicationStateChangeChange(e.value);
                  }}
                />
              ) : (
                <span>{stateTitle}</span>
              )}
            </h2>

            {application && application.uploadError && (
              <div className="moderator-contract-info__send-to-queue-button-wrapper">
                {application.uploadDate &&
                  new Date(application.updatedAt) <=
                    new Date(application.uploadDate) && (
                    <Button
                      type="button"
                      className="moderator-contract-info__send-to-queue-button"
                      onClick={sendToQueue}
                    >
                      Отправить на повторную выгрузку
                    </Button>
                  )}
                {application.externalId && (
                  <Button
                    type="button"
                    className="moderator-contract-info__send-to-queue-button"
                    onClick={clearError}
                  >
                    Очистить ошибку
                  </Button>
                )}
              </div>
            )}
          </div>
          {application?.educationLevel !==
            ApplicationEducationLevel.SECONDARY && (
            <div className="card">
              <b>{terms.CONTRACT_INFORMING}: </b>
              {application?.isInformed ? 'Да' : 'Нет'}
            </div>
          )}
          <div className="card">
            {application?.educationLevel ===
            ApplicationEducationLevel.SECONDARY ? (
              <div style={{ width: '15%' }}>
                <b>{terms.CONSENT}: </b>
                <ReactSelect<SelectOption>
                  isDisabled={
                    // Временно прикрываем этот функционал от модераторов (31.07.26)
                    // Если наступила новая ПК, а функционал закомментирован - удали эту строку.
                    // (role !== UserRole.ADMIN && role !== UserRole.MODERATOR) ||
                    role !== UserRole.ADMIN ||
                    state === ApplicationState.NOT_CREATED ||
                    state === ApplicationState.DRAFT ||
                    state === ApplicationState.ENROLLED ||
                    state === ApplicationState.WITHDRAWN ||
                    state === ApplicationState.WITHDRAWN_APPROVED ||
                    state === ApplicationState.REJECTED
                  }
                  options={agreementOptions}
                  value={findSelectOption(agreementOptions, selectedAgreement)}
                  onChange={e => handleHasAgreementChange(e.value)}
                />
              </div>
            ) : (
              <div>
                <b>{terms.CONSENT}: </b>
                {application?.agreement?.isAgreed ? 'Да' : 'Нет'}
              </div>
            )}

            {application?.agreement ? (
              <div className="info-agree">
                <div>
                  <b>{terms.CONSENT_SOURCE}: </b>
                  {application?.agreement?.isOnline ? 'ЕПГУ' : 'ВУЗ'}
                </div>
                {application.agreement?.agreeDate && (
                  <div>
                    <b>{terms.CREATION_DATE}: </b>
                    {renderDateString(application.agreement.agreeDate)}
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <div style={{ width: '100%' }}>
            <ReactSelect<SelectOption>
              placeholder="Оригинал документа об образовании"
              isDisabled={
                (role !== UserRole.ADMIN &&
                  role !== UserRole.INSTITUTE_STAFF &&
                  role !== UserRole.SPECIALIST) ||
                state === ApplicationState.NOT_CREATED ||
                state === ApplicationState.DRAFT ||
                state === ApplicationState.UNDER_CONSIDERATION
              }
              options={originalDocumentTypeSelectOptions}
              value={findSelectOption(
                originalDocumentTypeSelectOptions,
                selectedEducationDocumentType
              )}
              onChange={e => handleOriginalEducationDocumentTypeChange(e.value)}
            />
          </div>
          {originalSubmission &&
            (originalSubmission.submittedAt ||
              originalSubmission.withdrawnAt) &&
            `Оригинал документа об образовании ${
              originalSubmission.isSubmitted ? 'подан' : 'отозван'
            }: ${
              originalSubmission.isSubmitted
                ? renderDateString(originalSubmission.submittedAt)
                : renderDateString(originalSubmission.withdrawnAt)
            }`}
          <TextareaAutosize
            {...fields.comment}
            maxLength={2048}
            className="moderator-contract-info__comment-input"
            placeholder="Комментарий"
          />

          {(role === UserRole.MODERATOR ||
            role === UserRole.ADMIN ||
            role === UserRole.INSTITUTE_STAFF ||
            role === UserRole.SPECIALIST) && (
            <div className="moderator-contract-info__footer">
              <div className="moderator-contract-info__footer-btn-left">
                <div className="moderator-contract-info__footer-btn">
                  <NavLink to={navLink()} target="_blank">
                    <Button type="button">
                      Зайти в личный кабинет пользователя
                    </Button>
                  </NavLink>
                </div>
                {(role === UserRole.MODERATOR || role === UserRole.ADMIN) &&
                  application?.epguInfo?.createdFromEpgu && (
                    <div className="moderator-contract-info__footer-btn-left">
                      <div className="moderator-contract-info__footer-btn">
                        <a
                          href={sspvoLink()}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button type="button">
                            Зайти в личный кабинет пользователя ССПВО
                          </Button>
                        </a>
                      </div>
                    </div>
                  )}
              </div>

              {!application?.epguInfo?.createdFromEpgu &&
                state !== ApplicationState.WITHDRAWN_APPROVED &&
                state !== ApplicationState.DRAFT &&
                state !== ApplicationState.REJECTED &&
                role === UserRole.ADMIN && (
                  <div className="moderator-contract-info__footer-btn">
                    <Button
                      type="button"
                      className="moderator-contract-info__footer-btn-reject"
                      onClick={e => {
                        e.preventDefault();
                        openModal();
                      }}
                    >
                      Отозвать
                    </Button>
                  </div>
                )}

              {state === ApplicationState.ACCEPTED && (
                <div className="moderator-contract-info__footer-btn">
                  <Button
                    type="button"
                    theme="success"
                    onClick={allowApplication}
                  >
                    Допустить
                  </Button>
                </div>
              )}

              {((application?.citizenshipId !== 1 &&
                application?.epguInfo?.createdFromEpgu) ||
                state === ApplicationState.IN_WORK ||
                state === ApplicationState.DIRECTIONS_EDITED) && (
                <div className="moderator-contract-info__footer-btn">
                  {coordinatorState !== ApplicationFlag.SENT_TO_COORDINATOR && (
                    <Button
                      type="button"
                      className="moderator-contract-info__footer-btn-reject"
                      onClick={sendToCoordinator}
                    >
                      Отправить координатору
                    </Button>
                  )}

                  {canSendToExpert &&
                    expertState !== ApplicationFlag.SENT_TO_EXPERT &&
                    expertState !== ApplicationFlag.SENT_TO_EXPERT_AGAIN && (
                      <Button
                        type="button"
                        onClick={sendToExpert}
                        className="moderator-contract-info__footer-btn-reject"
                      >
                        Отправить в ЦЭИДО
                      </Button>
                    )}

                  {!application?.epguInfo?.createdFromEpgu && (
                    <Button
                      type="button"
                      onClick={
                        state === ApplicationState.IN_WORK
                          ? () => {
                              if (
                                application?.epguInfo?.createdFromEpgu &&
                                !application?.epguInfo?.reasonRejectExternalId
                              )
                                dispatch(
                                  showModal({ name: 'SET_EPGU_REJECT_REASON' })
                                );
                              else rejectApplication();
                            }
                          : rejectApplicationEditing
                      }
                      className="moderator-contract-info__footer-btn-reject"
                    >
                      Отказать
                    </Button>
                  )}

                  {!application?.epguInfo?.createdFromEpgu &&
                    state !== ApplicationState.DIRECTIONS_EDITED && (
                      <Button type="button" onClick={sendToEditing}>
                        Отправить на редактирование
                      </Button>
                    )}

                  {!application?.epguInfo?.createdFromEpgu && (
                    <Button
                      type="button"
                      onClick={acceptApplication}
                      theme="success"
                    >
                      Одобрить
                    </Button>
                  )}
                </div>
              )}

              {state === ApplicationState.WITHDRAWN &&
                !application?.epguInfo?.createdFromEpgu &&
                (role === UserRole.MODERATOR ||
                  role === UserRole.ADMIN ||
                  role === UserRole.INSTITUTE_STAFF ||
                  role === UserRole.SPECIALIST) && (
                  <div className="moderator-contract-info__footer-btn">
                    <Button
                      type="button"
                      onClick={acceptWithdraw}
                      className="moderator-contract-info__footer-btn-reject"
                    >
                      Одобрить отзыв
                    </Button>
                    <Button
                      type="button"
                      onClick={declineWithdraw}
                      theme="success"
                    >
                      Отказать в отзыве
                    </Button>
                  </div>
                )}
            </div>
          )}

          {(role === UserRole.EXPERT || role === UserRole.COORDINATOR) && (
            <div className="moderator-contract-info__footer">
              <div />
              {role === UserRole.EXPERT &&
                (expertState === ApplicationFlag.SENT_TO_EXPERT ||
                  expertState === ApplicationFlag.SENT_TO_EXPERT_AGAIN) &&
                expertId && (
                  <div className="moderator-contract-info__footer-btn">
                    <Button type="button" onClick={requestAdditionalFiles}>
                      Запросить дополнительные документы
                    </Button>
                    <Button
                      type="button"
                      onClick={rejectByExpert}
                      className="moderator-contract-info__footer-btn-reject"
                    >
                      Отказать
                    </Button>
                    <Button
                      type="button"
                      onClick={approveByExpert}
                      theme="success"
                    >
                      Одобрить
                    </Button>
                  </div>
                )}
              {role === UserRole.COORDINATOR &&
                coordinatorState === ApplicationFlag.SENT_TO_COORDINATOR &&
                coordinatorId && (
                  <div className="moderator-contract-info__footer-btn">
                    <Button
                      type="button"
                      onClick={rejectByCoordinator}
                      className="moderator-contract-info__footer-btn-reject"
                    >
                      Отказать
                    </Button>
                    <Button
                      type="button"
                      onClick={approveByCoordinator}
                      theme="success"
                    >
                      Одобрить
                    </Button>
                  </div>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModeratorStatementsIdPage;
